import React from 'react';
import { Form, Field } from 'react-final-form';
import { Box, Flex } from 'rebass';
import { useStore } from 'effector-react';

import { Trans, t } from '@lingui/macro';
import { Button, Typography, Dialog } from '@oca/ui';
import { TextFieldWrapper, SelectWrapper } from '@lib/final-form-oca-ui';
import { i18n } from '@lib/i18n';
import {
  $isCreateModalOpen,
  closeCreateModal,
  createPayrollRequestEffect,
  toSubmitErrors,
} from '../model/payroll';

const monthOptions = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

const currentYear = new Date().getFullYear();
const yearOptions = [
  { value: currentYear, label: String(currentYear) },
  { value: currentYear - 1, label: String(currentYear - 1) },
];

const REASON_MAX = 1000;
const NOTES_MAX = 4000;

export const RequestModal = () => {
  const isOpen = useStore($isCreateModalOpen);

  if (!isOpen) return null;

  return (
    <Dialog title={i18n._(t`Request Payroll Slip`)} onClose={closeCreateModal}>
      <Box style={{ minWidth: 420, maxWidth: 440 }}>
        <Form
          initialValues={{
            month: new Date().getMonth() + 1,
            year: currentYear,
          }}
          validate={validate}
          onSubmit={handleSubmit}
          render={({ handleSubmit: onSubmitForm, submitError, submitting }) => (
            <form onSubmit={onSubmitForm}>
              <Box mb={20}>
                <Field
                  name="month"
                  label={<Trans>Salary Month</Trans>}
                  options={monthOptions}
                  component={SelectWrapper}
                  parse={val => val && val.value}
                  format={val =>
                    monthOptions.find(m => m.value === (val.value || val))
                  }
                  spaceAfter
                />
              </Box>

              <Box mb={20}>
                <Field
                  name="year"
                  label={<Trans>Salary Year</Trans>}
                  options={yearOptions}
                  component={SelectWrapper}
                  parse={val => val && val.value}
                  format={val =>
                    yearOptions.find(y => y.value === (val.value || val))
                  }
                  spaceAfter
                />
              </Box>

              <Box mb={20}>
                <Field
                  name="reason"
                  label={<Trans>Reason</Trans>}
                  placeholder={i18n._(t`e.g. Bank loan review`)}
                  component={TextFieldWrapper}
                  maxLength={REASON_MAX}
                  required
                  spaceAfter
                />
              </Box>

              <Box mb={20}>
                <Field
                  name="notes"
                  label={<Trans>Additional Notes (Optional)</Trans>}
                  placeholder={i18n._(t`Any specific instructions or details`)}
                  component={TextFieldWrapper}
                  maxLength={NOTES_MAX}
                  spaceAfter
                />
              </Box>

              {submitError && (
                <Box
                  mb={3}
                  p={2}
                  bg="#fff1f0"
                  style={{ border: '1px solid #ffa39e', borderRadius: 6 }}
                >
                  <Typography color="#cf1322" fontSize={13}>
                    {submitError}
                  </Typography>
                </Box>
              )}

              <Flex justifyContent="flex-end" style={{ gap: '10px' }}>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={closeCreateModal}
                  disabled={submitting}
                >
                  <Trans>Cancel</Trans>
                </Button>
                <Button variant="primary" type="submit" disabled={submitting}>
                  <Trans>Submit Request</Trans>
                </Button>
              </Flex>
            </form>
          )}
        />
      </Box>
    </Dialog>
  );
};

function validate(values) {
  const errors = {};
  if (!values.month) {
    errors.month = <Trans>Please select a month</Trans>;
  }
  if (!values.year) {
    errors.year = <Trans>Please select a year</Trans>;
  }
  const reason = (values.reason || '').trim();
  if (!reason) {
    errors.reason = <Trans>Please enter the reason for this request</Trans>;
  } else if (reason.length > REASON_MAX) {
    errors.reason = <Trans>Reason must be at most 1000 characters</Trans>;
  }
  if (values.notes && values.notes.length > NOTES_MAX) {
    errors.notes = <Trans>Notes must be at most 4000 characters</Trans>;
  }
  return errors;
}

async function handleSubmit({ month, year, reason, notes }) {
  try {
    await createPayrollRequestEffect({
      month,
      year,
      reason: reason.trim(),
      ...(notes && notes.trim() ? { notes: notes.trim() } : {}),
    });
    return null;
  } catch (error) {
    return toSubmitErrors(error, ['month', 'year', 'reason', 'notes']);
  }
}
